/**
 * PORTED — RO-3-11 / #44304.
 * Origin: `excellent/app/database/objects/new/_new-client.tsx` (433 LOC).
 *
 * Changes, and only these:
 *   `"use client"`                          -> dropped (inert in this host)
 *   `useRouter` from next/navigation        -> `useNavigate` from @excellent/ui/router
 *   `router.push(...)`                      -> `navigate.push(...)`
 *   `createObjectTypeAction(input)`         -> `apiCall(objectTypes.create, { body })`
 *   `NewObjectTypeInput` from `./actions`   -> `CreateObjectTypeBody` (the contract)
 *   the states editor's `h-7` className        -> the `size` prop  ← see below
 *
 * The action swap is the reason this file could be ported at all: RO-2-03 already
 * converted `createObjectTypeAction` to `objectTypes.create` and mounted it, so the
 * wizard's one write reaches the SAME core `createObjectType` (origin 'user') it
 * always did, with the same reserved-slug refusal and the same structured reason
 * codes the `reasonCopy` map below renders. Nothing about the walk changed.
 *
 * ── The one substantive fix, and why it is a fix and not a drift ──
 *
 * The origin has an entry in `check-button-consistency`'s frozen ALLOWLIST
 * (`excellent/app/database/objects/new/_new-client.tsx: 1`) for a Pass-3
 * vertical-metric repaint: the states editor's draft input carried an `h-7`
 * className.
 *
 * Per-file allowances are frozen and shrink-only, so a NEW file's allowance is 0
 * — copying that line byte-identically would create a real violation, not inherit
 * a grandfathered one (this is filed as a class in #44455 after it cost #44387 a
 * reject). The fix is the one the atom was built for: the input atom's `default`
 * size already resolves to `h-7` (`data-[size=default]:h-7`), so the repaint was a
 * no-op. Passing `size="default"` states the intent through the prop and renders
 * byte-identical output. The allowlist was NOT grown.
 *
 * (Written in prose rather than as tag literals on purpose: Pass 3 scans with a
 * plain `<Input\b` regex and does not skip comments, so quoting the offending JSX
 * in a header makes the gate fail on the documentation of the fix. Learned here.)
 *
 * `StorageOption`'s `button-ignore:` comment travels with the code and is kept
 * verbatim — that is an INLINE hatch on a genuine radio-style option card, not an
 * allowlist entry, and it is still true here.
 */

import { useState } from "react";
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  Icon,
  Input,
  SetupDriver,
  cn,
  type SetupCard,
  type SetupInputContext,
} from "@excellent/ui";
import { useNavigate } from "@excellent/ui/router";
import { apiCall } from "@excellent/ui/api";
import {
  objectTypes,
  type CreateObjectTypeBody,
} from "@excellent/ui/api/contracts/database/object-types";

/**
 * The object-type creation wizard walk (PRD §3.4), driven by `SetupDriver` — the
 * one blessed stepper. Prompt cards collect the definition; a final review
 * action card commits it through the `objectTypes.create` endpoint (origin
 * 'user', same validation as the agent tool) and routes to the new kind's detail
 * page. Core reason codes surface as an inline alert.
 *
 *   1. Identity — name (slug + label) + optional icon.
 *   2. Storage model — commitment (lifecycle records + state list) vs party
 *      (+ person|organization base), explained in plain language.
 *   3. Review & create.
 */

const SLUG_RE = /^[a-z][a-z0-9_]{1,63}$/;

interface Answers {
  identity?: { label: string; kind: string; icon: string };
  storage?: StorageValue;
}

interface StorageValue {
  storage: "commitment" | "party";
  party_base: "person" | "organization";
  /** Ordered lifecycle states (commitment kinds); the last is treated terminal. */
  states: string[];
}

const DEFAULT_STORAGE: StorageValue = {
  storage: "commitment",
  party_base: "organization",
  states: ["open", "in_progress", "done"],
};

/** Derive a lowercase slug from a label ("Purchase Order" → "purchase_order"). */
function slugify(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);
}

// ── custom inputs (own components so they can hold local draft state) ──────────

interface IdentityValue {
  label: string;
  kind: string;
  icon: string;
  /** True once the user hand-edits the slug — stops auto-deriving from the label. */
  slugTouched: boolean;
}

function IdentityInput({ ctx }: { ctx: SetupInputContext }) {
  const v: IdentityValue =
    ctx.value && typeof ctx.value === "object"
      ? (ctx.value as IdentityValue)
      : { label: "", kind: "", icon: "", slugTouched: false };
  const set = (next: Partial<IdentityValue>) => ctx.setValue({ ...v, ...next });
  const slugValid = SLUG_RE.test(v.kind);
  return (
    <div className="flex flex-col gap-4">
      <label className="flex flex-col gap-1.5">
        <span className="type-body font-medium text-foreground">
          Name <span className="text-destructive">•</span>
        </span>
        <Input
          data-setup-focus
          value={v.label}
          onChange={(e) => {
            const label = e.target.value;
            set({ label, ...(v.slugTouched ? {} : { kind: slugify(label) }) });
          }}
          placeholder="e.g. Shipment"
        />
        <span className="type-subtext text-muted-foreground">
          The singular, human-readable name for one of these.
        </span>
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="type-body font-medium text-foreground">
          Kind slug <span className="text-destructive">•</span>
        </span>
        <Input
          value={v.kind}
          onChange={(e) => set({ kind: e.target.value.toLowerCase(), slugTouched: true })}
          placeholder="shipment"
          className="font-mono"
        />
        <span
          className={cn(
            "type-subtext",
            v.kind && !slugValid ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {v.kind && !slugValid
            ? "Lowercase letters, numbers, underscores; must start with a letter."
            : "The stable machine slug — used in URNs and joins. Can't change later."}
        </span>
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="type-body font-medium text-foreground">Icon</span>
        <Input
          value={v.icon}
          onChange={(e) => set({ icon: e.target.value })}
          placeholder="package (optional)"
        />
        <span className="type-subtext text-muted-foreground">
          An optional lucide icon name shown beside the object.
        </span>
      </label>
    </div>
  );
}

const identityCanSave = (value: unknown): boolean => {
  const v = value as IdentityValue | undefined;
  return Boolean(v && v.label.trim() && SLUG_RE.test(v.kind));
};

function StatesEditor({
  states,
  onChange,
}: {
  states: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const t = draft.trim().toLowerCase();
    if (!t || states.includes(t)) return;
    onChange([...states, t]);
    setDraft("");
  };
  const remove = (s: string) => onChange(states.filter((x) => x !== s));
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border/60 p-3">
      <span className="type-subtext text-muted-foreground">
        Lifecycle states, in order. Records start in the first; the last reads as
        terminal.
      </span>
      <div className="flex flex-wrap gap-1.5">
        {states.map((s, i) => (
          <span
            key={s}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-2.5 py-0.5 type-subtext text-foreground"
          >
            <span className="tabular-nums text-muted-foreground">{i + 1}</span>
            {s}
            <button
              type="button"
              onClick={() => remove(s)}
              aria-label={`Remove ${s}`}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="x" size="xs" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="Add a state…"
          size="default"
        />
        <Button type="button" variant="outline" size="sm" onClick={add} disabled={!draft.trim()}>
          Add
        </Button>
      </div>
    </div>
  );
}

function StorageInput({ ctx }: { ctx: SetupInputContext }) {
  const v: StorageValue =
    ctx.value && typeof ctx.value === "object" ? (ctx.value as StorageValue) : DEFAULT_STORAGE;
  const set = (next: Partial<StorageValue>) => ctx.setValue({ ...v, ...next });
  return (
    <div className="flex flex-col gap-3">
      <StorageOption
        on={v.storage === "commitment"}
        onSelect={() => set({ storage: "commitment" })}
        title="Lifecycle record"
        body="A thing that moves through states over time — a shipment, an onboarding, a claim. It gets a lifecycle, an event trail, and its own object page."
      />
      {v.storage === "commitment" ? (
        <StatesEditor states={v.states} onChange={(states) => set({ states })} />
      ) : null}
      <StorageOption
        on={v.storage === "party"}
        onSelect={() => set({ storage: "party" })}
        title="Party overlay"
        body="A typed kind of person or company — a vendor, a partner, a patient. It reuses identity resolution and merge, wearing this kind as a tag on the base record."
      />
      {v.storage === "party" ? (
        <div className="flex gap-2 pl-1">
          <BaseChip on={v.party_base === "organization"} onSelect={() => set({ party_base: "organization" })} label="Company" />
          <BaseChip on={v.party_base === "person"} onSelect={() => set({ party_base: "person" })} label="Person" />
        </div>
      ) : null}
    </div>
  );
}

function StorageOption({
  on,
  onSelect,
  title,
  body,
}: {
  on: boolean;
  onSelect: () => void;
  title: string;
  body: string;
}) {
  return (
    // button-ignore: selectable radio-style storage-model card, not an action button
    <button
      type="button"
      data-setup-focus={on ? "" : undefined}
      onClick={onSelect}
      className={cn(
        "w-full rounded-md border p-3 text-left transition-colors",
        on ? "border-primary/50 bg-primary/5" : "border-border/60 hover:bg-secondary",
      )}
    >
      <div className="flex items-center gap-2 type-body font-medium text-foreground">
        <span
          className={cn(
            "inline-flex size-3.5 items-center justify-center rounded-full border",
            on ? "border-primary" : "border-muted-foreground/40",
          )}
        >
          {on ? <span className="size-1.5 rounded-full bg-primary" /> : null}
        </span>
        {title}
      </div>
      <p className="mt-1 pl-6 type-subtext leading-body text-muted-foreground">{body}</p>
    </button>
  );
}

function BaseChip({ on, onSelect, label }: { on: boolean; onSelect: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "rounded-full border px-3 py-1 type-subtext transition-colors",
        on ? "border-primary/50 bg-primary/10 text-foreground" : "border-border/60 text-muted-foreground hover:bg-secondary",
      )}
    >
      {label}
    </button>
  );
}

// ── the card series ────────────────────────────────────────────────────────────

function wizardCards(answers: Answers): SetupCard[] {
  const identity = answers.identity;
  const storage = answers.storage ?? DEFAULT_STORAGE;
  const reviewLines = identity
    ? [
        `${identity.label} · ${identity.kind}`,
        storage.storage === "commitment"
          ? `Lifecycle record · states: ${storage.states.join(" → ") || "none"}`
          : `Party overlay · ${storage.party_base}`,
      ]
    : [];
  return [
    {
      key: "identity",
      mode: "prompt",
      title: "Name your object",
      question: "What do you want to track?",
      sublabel: "Give it a name and a stable slug — the rest of the setup keys off this.",
      initialValue:
        answers.identity ?? { label: "", kind: "", icon: "", slugTouched: false },
      canSave: identityCanSave,
      renderInput: (ctx) => <IdentityInput ctx={ctx} />,
    },
    {
      key: "storage",
      mode: "prompt",
      title: "How is it stored?",
      question: "What kind of thing is it?",
      sublabel: "This decides the store it rides on — you can't change it later, so pick the closest fit.",
      initialValue: answers.storage ?? DEFAULT_STORAGE,
      canSave: (v) => {
        const s = v as StorageValue | undefined;
        if (!s) return false;
        if (s.storage === "commitment") return s.states.length > 0;
        return s.party_base === "person" || s.party_base === "organization";
      },
      renderInput: (ctx) => <StorageInput ctx={ctx} />,
    },
    {
      key: "review",
      mode: "action",
      badge: { label: "Review", intent: "info" },
      title: "Create this object type",
      body: identity
        ? `“${identity.label}” will become a first-class object — with its own page, notes, tags, and records.`
        : "Fill in the earlier steps first.",
      reasons: reviewLines,
      ctaLabel: "Create object type",
    },
  ];
}

// ── the host ────────────────────────────────────────────────────────────────────

export function NewObjectTypeWizard() {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState<Answers>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onAnswer = (card: { key: string }, value: unknown) => {
    setAnswers((a) => ({ ...a, [card.key]: value as never }));
  };

  const reasonCopy = (reason: string, detail?: string): string => {
    switch (reason) {
      case "reserved-kind":
        return `“${detail ?? "that slug"}” is reserved — it shadows a built-in kind or a table. Pick a different slug.`;
      case "kind-exists":
        return "An object type with that slug already exists.";
      case "invalid-kind":
        return "The slug must be a lowercase slug: [a-z][a-z0-9_].";
      case "party-base-required":
        return "Choose whether this party is a person or a company.";
      default:
        return "Couldn't create the object type. Check the definition and try again.";
    }
  };

  const submit = async () => {
    const identity = answers.identity;
    if (!identity) {
      setError("Name the object type first.");
      return;
    }
    const storage = answers.storage ?? DEFAULT_STORAGE;
    setSubmitting(true);
    setError(null);
    const input: CreateObjectTypeBody = {
      kind: identity.kind,
      label: identity.label.trim(),
      label_plural: null,
      description: null,
      icon: identity.icon.trim() || null,
      storage: storage.storage,
      party_base: storage.storage === "party" ? storage.party_base : null,
      state_machine:
        storage.storage === "commitment" && storage.states.length > 0
          ? JSON.stringify({
              states: storage.states,
              terminal: storage.states.slice(-1),
            })
          : null,
    };
    // A transport failure (offline, 500) throws; a DOMAIN refusal comes back as
    // `{ok:false, reason}` in the payload, exactly as the server action did. Both
    // land in the same inline alert — the wizard never shows a raw stack.
    try {
      const res = await apiCall(objectTypes.create, { body: input });
      if (res.ok) {
        navigate.push(`/database/objects/${encodeURIComponent(res.kind)}`);
        return;
      }
      setSubmitting(false);
      setError(reasonCopy(res.reason, res.detail));
    } catch (e) {
      setSubmitting(false);
      setError(e instanceof Error ? e.message : reasonCopy("unknown"));
    }
  };

  const onAct = (card: { key: string }) => {
    if (card.key === "review") void submit();
  };

  return (
    <Card className="max-w-2xl p-6">
      {error ? (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      {submitting ? (
        <div className="flex items-center gap-2 py-8 type-body text-muted-foreground">
          <Icon name="loader-circle" size="sm" className="animate-spin" />
          Creating your object type…
        </div>
      ) : (
        <SetupDriver
          cards={wizardCards(answers)}
          showDismiss={false}
          onAnswer={onAnswer}
          onAct={onAct}
          onExhausted={() => navigate.push("/database/objects")}
          copy={{
            finishTitle: () => "Ready to create",
            finishBody: () => "Press Create on the review card to add it.",
          }}
        />
      )}
    </Card>
  );
}
